/**
 * API client for the Revenue Goals group (monthly target vs committed).
 *
 * Backend: oh_revenue_goals Django app, alfred-suffixed endpoints under
 * /api/v1/oh_revenue_goals/goal/. Every `apiEndpoints.X` reference lives
 * INSIDE a function body (TDZ-safety — mirrors lifeCatchUps.js).
 */
import { sentryLogger } from 'sentry'

import { apiEndpoints } from './common'

import { makeRequestUrl } from '../app/helpers'
import { get, patch, post, put, remove } from '../request'

// month: 'YYYY-MM'
const fetchRevenueGoalSummary = async (month) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.revenueGoals}/summary/alfred`, { month })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching revenue goal summary')
    throw error
  }
}

const fetchRevenueGoals = async (month) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.revenueGoals}/alfred`, { month })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching revenue goals')
    throw error
  }
}

// Upsert by (centre_uuid, month) — the Set Targets grid saves cell by cell.
const upsertRevenueGoal = async (payload) => {
  try {
    const response = await post(`${apiEndpoints.revenueGoals}/alfred`, payload)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in saving revenue goal')
    throw error
  }
}

const updateRevenueGoal = async (goalUuid, payload) => {
  try {
    const response = await patch(
      `${apiEndpoints.revenueGoals}/${goalUuid}/alfred`,
      payload
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in updating revenue goal')
    throw error
  }
}

// My Day — the centre team's ranked worklist (replaces the chase list).
const fetchMyDay = async (month, centreUuid) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.revenueGoals}/my-day/alfred`, {
        month,
        centre_uuid: centreUuid
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching my day')
    throw error
  }
}

// Pipeline — the centre's picked-up parents. All three share one endpoint and
// every one returns the WHOLE refreshed pipeline, so the caller never has to
// reconcile local state after a change.
const fetchPipeline = async (centreUuid, addedBy) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.revenueGoals}/pipeline/alfred`, {
        centre_uuid: centreUuid,
        added_by: addedBy || undefined
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching pipeline')
    throw error
  }
}

const addToPipeline = async (centreUuid, parentUserUuid, details = {}) => {
  try {
    const response = await post(`${apiEndpoints.revenueGoals}/pipeline/alfred`, {
      centre_uuid: centreUuid,
      parent_user_uuid: parentUserUuid,
      ...details
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in adding to pipeline')
    throw error
  }
}

const removeFromPipeline = async (centreUuid, parentUserUuid) => {
  try {
    const response = await remove(
      makeRequestUrl(`${apiEndpoints.revenueGoals}/pipeline/alfred`, {
        centre_uuid: centreUuid,
        parent_user_uuid: parentUserUuid
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in removing from pipeline')
    throw error
  }
}

const fetchRevenueGoalChase = async (month, centreUuid) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.revenueGoals}/chase/alfred`, {
        month,
        centre_uuid: centreUuid
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching revenue goal chase list')
    throw error
  }
}

const fetchRevenueGoalHqRollup = async (month) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.revenueGoals}/hq-rollup/alfred`, { month })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching revenue goal HQ rollup')
    throw error
  }
}

// Re-extract ONE parent's signals synchronously after a call is logged, so the
// list re-ranks on what was just logged (the tight daily loop). Best-effort on
// the server; the caller refetches regardless of the outcome.
const extractParentSignals = async (parentUuid, centreUuid) => {
  try {
    const response = await post(
      `${apiEndpoints.revenueGoals}/extract-parent/alfred`,
      { parent_uuid: parentUuid, centre_uuid: centreUuid || undefined }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in re-extracting parent signals')
    throw error
  }
}

const refreshRevenueGoalSignals = async (centreUuid) => {
  try {
    const response = await post(
      `${apiEndpoints.revenueGoals}/refresh-signals/alfred`,
      { centre_uuid: centreUuid }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in refreshing recharge signals')
    throw error
  }
}

// Planner (Phase B): view='monthly' (HQ grid) | 'weekly' (one centre cadence).
const fetchRevenuePlanner = async (month, view, centreUuid) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.revenueGoals}/planner/alfred`, {
        month,
        view,
        ...(centreUuid ? { centre_uuid: centreUuid } : {})
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching revenue planner')
    throw error
  }
}

// Push PayFlow "Finance says" into committed (override = finance − Alfred).
const syncRevenueGoalFinance = async (month) => {
  try {
    const response = await post(
      `${apiEndpoints.revenueGoals}/sync-finance/alfred`,
      { month }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in syncing finance actuals')
    throw error
  }
}

// Enrollment Directory (Revenue Goals v2 · Screen 1): one row per enrolment
// across every programme, filterable All / Active / Inactive.
const fetchEnrollmentDirectory = async (centreUuid, statusFilter, month) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.revenueGoals}/directory/alfred`, {
        centre_uuid: centreUuid,
        status: statusFilter || 'all',
        // Roster for the SELECTED month (user 2026-08-09) — a past month shows
        // who was active then. Omitted when not provided (current, month-less).
        ...(month ? { month } : {})
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching enrollment directory')
    throw error
  }
}

// Pending Centre-Parent Chats: open chat threads at the centre, awaiting-reply
// first. { rows, counts{ all, awaiting_reply } }.
const fetchPendingChats = async (centreUuid) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.revenueGoals}/pending-chats/alfred`, {
        centre_uuid: centreUuid
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching pending chats')
    throw error
  }
}

// Interaction Audit: prospects who visited but still need an interaction logged
// (never logged / stale). { rows, counts{ all, never_logged } }.
const fetchInteractionsNotLogged = async (centreUuid) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.revenueGoals}/interactions-not-logged/alfred`, {
        centre_uuid: centreUuid
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching interactions-not-logged')
    throw error
  }
}

// Focus for the day: the four daily must-do targets scored from live data.
// { score, score_of, targets:[{key,label,open,target,hit,rows,legs,...}] }.
const fetchFocus = async (centreUuid, day) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.revenueGoals}/focus/alfred`, {
        centre_uuid: centreUuid,
        date: day
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching focus')
    throw error
  }
}

// Trends: the centre's daily connect input over a date range (the one target
// with real day-by-day history), plus today's standing for the stock targets.
// { start, end, goal_total, days:[{date,total,goal,hit,legs}], hit_rate, streak, standing }.
const fetchTrends = async (centreUuid, start, end) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.revenueGoals}/trends/alfred`, {
        centre_uuid: centreUuid,
        start,
        end
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching trends')
    throw error
  }
}

// Manager Cockpit: every in-scope centre's Focus score /4 for one day (worst
// first) with the counts behind it. Today = live, past = ledger.
// { day, is_live, rows:[{centre, centre_uuid, score, score_of, chats_open, ...}] }.
const fetchCockpit = async (day) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.revenueGoals}/cockpit/alfred`, { date: day })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching cockpit')
    throw error
  }
}

// Centre score trends: each in-scope centre's daily score /4 over a range.
// { start, end, centres:[{centre, centre_uuid, days:[{date, score}]}] }.
const fetchCentreScoreTrends = async (start, end) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.revenueGoals}/centre-trends/alfred`, { start, end })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching centre score trends')
    throw error
  }
}

// Daily Connected: how many parents each team member connected with on a day,
// with programme + cohort split. { total_connected, people, programmes, ... }.
const fetchDailyConnected = async (centreUuid, day) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.revenueGoals}/daily-connected/alfred`, {
        centre_uuid: centreUuid,
        date: day
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching daily-connected')
    throw error
  }
}

// "Why isn't it converting?" reports (recharge / discovery / trials) for a
// centre + month. { report, title, eligible, succeeded, not_done, buckets, ... }.
const fetchConversionReport = async (centreUuid, month, type) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.revenueGoals}/report/alfred`, {
        centre_uuid: centreUuid,
        month,
        type
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching conversion report')
    throw error
  }
}

// Afterschool Funnel (Revenue Goals v2): the centre's whole afterschool list
// bucketed into At Risk / Winback / Upsell / Tour-Trial-Walk-in / Dormant.
const fetchAfterschoolFunnel = async (month, centreUuid) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.revenueGoals}/funnel/alfred`, {
        month,
        centre_uuid: centreUuid
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching afterschool funnel')
    throw error
  }
}

// Enquiry-pipeline conversion ratios (HQ knob, per intent).
const fetchConversionRatios = async () => {
  try {
    const response = await get(
      `${apiEndpoints.revenueGoals}/conversion-ratios/alfred`
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching conversion ratios')
    throw error
  }
}

// payload: { centre_uuid|null, high_ratio, medium_ratio, low_ratio } (0-1)
const updateConversionRatio = async (payload) => {
  try {
    const response = await put(
      `${apiEndpoints.revenueGoals}/conversion-ratios/alfred`,
      payload
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in updating conversion ratio')
    throw error
  }
}

export {
  fetchRevenueGoalSummary,
  fetchRevenueGoals,
  fetchRevenueGoalChase,
  fetchMyDay,
  fetchPipeline,
  addToPipeline,
  removeFromPipeline,
  fetchEnrollmentDirectory,
  fetchPendingChats,
  fetchInteractionsNotLogged,
  fetchDailyConnected,
  fetchFocus,
  fetchTrends,
  fetchCockpit,
  fetchCentreScoreTrends,
  fetchAfterschoolFunnel,
  fetchRevenueGoalHqRollup,
  refreshRevenueGoalSignals,
  extractParentSignals,
  fetchConversionReport,
  fetchRevenuePlanner,
  syncRevenueGoalFinance,
  fetchConversionRatios,
  updateConversionRatio,
  upsertRevenueGoal,
  updateRevenueGoal
}
